/**
 * Impersonation client-side helpers.
 *
 * State is parked entirely in sessionStorage so the original super-admin's
 * tab (which uses localStorage) is untouched, and closing the impersonation
 * tab automatically tears the session down.
 */

const FLAG_KEY = '__impersonation'
const IMPERSONATOR_KEY = '__impersonator'
const IMPERSONATION_ID_KEY = '__impersonation_id'
const PENDING_AUTH_KEY = '__pending-impersonation-auth'

export interface Impersonator {
  user_id: number
  name: string
  email: string
}

/**
 * Read-once at module load. Zustand's `persist` middleware uses this to
 * decide which storage backs the user store for the lifetime of the tab.
 * Reading it on every call would let it drift mid-session if other code
 * touched the flag.
 */
let cachedFlag: boolean | null = null
export function isImpersonatingNow(): boolean {
  if (cachedFlag !== null) return cachedFlag
  if (typeof window === 'undefined') return false
  try {
    cachedFlag = sessionStorage.getItem(FLAG_KEY) === '1'
  } catch {
    cachedFlag = false
  }
  return cachedFlag
}

export function setImpersonationFlag(): void {
  try { sessionStorage.setItem(FLAG_KEY, '1') } catch {}
  cachedFlag = true
}

export function getImpersonator(): Impersonator | null {
  try {
    const raw = sessionStorage.getItem(IMPERSONATOR_KEY)
    if (!raw) return null
    return JSON.parse(raw) as Impersonator
  } catch {
    return null
  }
}

export function setImpersonator(value: Impersonator): void {
  try { sessionStorage.setItem(IMPERSONATOR_KEY, JSON.stringify(value)) } catch {}
}

export function getImpersonationId(): number | null {
  const raw = sessionStorage.getItem(IMPERSONATION_ID_KEY)
  return raw ? Number(raw) : null
}

export function setImpersonationId(id: number): void {
  try { sessionStorage.setItem(IMPERSONATION_ID_KEY, String(id)) } catch {}
}

export function popPendingAuth(): unknown | null {
  try {
    const raw = sessionStorage.getItem(PENDING_AUTH_KEY)
    if (!raw) return null
    sessionStorage.removeItem(PENDING_AUTH_KEY)
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function setPendingAuth(authPayload: unknown): void {
  try { sessionStorage.setItem(PENDING_AUTH_KEY, JSON.stringify(authPayload)) } catch {}
}

export function clearImpersonationState(): void {
  try {
    sessionStorage.removeItem(FLAG_KEY)
    sessionStorage.removeItem(IMPERSONATOR_KEY)
    sessionStorage.removeItem(IMPERSONATION_ID_KEY)
    sessionStorage.removeItem(PENDING_AUTH_KEY)
    sessionStorage.removeItem('user-storage')
  } catch {}
  cachedFlag = false
}

/**
 * Fire-and-forget call to the backend to invalidate the current
 * impersonation session, then clear local state. Caller decides whether to
 * close the tab or redirect after this resolves.
 */
export async function endImpersonation(apiUrl: string, bearerToken: string | null): Promise<void> {
  if (bearerToken) {
    try {
      await fetch(`${apiUrl}/auth/end-impersonation`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${bearerToken}`,
        },
      })
    } catch {
      // best effort — local cleanup still proceeds
    }
  }
  clearImpersonationState()
}
